// ScenarioEvent.tsx
// React & Hooks
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactTabulator } from 'react-tabulator';
import type { CellComponent, ColumnDefinition, RowComponent, Tabulator } from 'tabulator-tables';

import ModalDetectionArea from '@/domains/scenario/components/ModalDetectionArea';
// Custom Hooks
import useEventManagement from '@/domains/scenario/hooks/useEventManagement';
// Types
import type {
  CreateDetectionRequest,
  DetectionDetailData,
  UpdateDetectionRequest,
} from '@/domains/scenario/types/event-management.types';
// import useSiteRoad from '@/hooks/useSiteRoad';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';
import ButtonGroup from '@/shared/components/ui/ButtonGroup/ButtonGroup';
// Components
import ContainerCard from '@/shared/components/ui/ContainerCard/ContainerCard';
import DetailForm from '@/shared/components/ui/DetailForm/DetailForm';
import Filtering from '@/shared/components/ui/Filtering/Filtering';
import LogList from '@/shared/components/ui/LogList/LogList';
import Select from '@/shared/components/ui/Select/Select';
import useCommonCodes from '@/shared/hooks/useCommonCodes';
import { remoteSortHandler, useServerTable } from '@/shared/hooks/useServerTable';
import useUnmappedSiteRoad from '@/shared/hooks/useUnmappedSiteRoad';

interface EventFormValues {
  detection_id: string;
  name: string;
  site_id: string;
  site_name: string;
  road_name: string;
  road_id: string;
  type: string;
  type_value: string;
  polygons: string;
}

interface ScenarioEvent {
  detection_id: string;
  site_name: string;
  road_name: string;
  name: string;
  type_value: string;
  updated_time: string;
}

const commonColumnSettings = {
  hozAlign: 'center',
  headerHozAlign: 'center',
  resizable: false,
};

// Columns whose `field` matches the backend allowed-sort whitelist keys.
// detection endpoint allows: id (detection_id) | updated_time.
const DETECTION_SORT_FIELD_MAP: Record<string, string> = {
  detection_id: 'id',
  updated_time: 'updated_time',
};

const EventManagementPage = () => {
  const { t } = useTranslation();
  const tbRef = useRef<Tabulator | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNewClicked, setIsNewClicked] = useState(false);
  const [disabledForm, setDisabledForm] = useState(true);
  const disabledId = true;
  const [hasChangesUpdate, setHasChangesUpdate] = useState(false);
  const [hasChangesCreate, setHasChangesCreate] = useState(false);

  const hasChangesUpdateRef = useRef(hasChangesUpdate);
  const hasChangesCreateRef = useRef(hasChangesCreate);
  const isNewClickedRef = useRef(isNewClicked);

  const [newId, setNewId] = useState<string>('');

  const [resource] = useState('resource=detection');
  const [optionParams] = useState('upper-code=SCNR_TP');
  const [siteRoadParams] = useState('site_type=102001&site_type=102002&site_type=102003');

  const [isRowSelected, setIsRowSelected] = useState(false);
  const [isEnabledModal, setIsEnableModal] = useState(false);

  // Params
  const table = useServerTable<{
    input?: string;
    site_id?: string;
    road_id?: string;
    detection_type?: string;
  }>({
    defaultSort: { field: 'updated_time', dir: 'desc' },
    defaultSize: 10,
  });
  const queryParams = table.queryString;
  const [selectedEvent, setSelectedEvent] = useState<{
    Detection_ID: string | null;
  }>({
    Detection_ID: null,
  });

  const selectedEventRef = useRef(selectedEvent);

  // Values
  const [formValues, setFormValues] = useState<EventFormValues>({
    detection_id: '',
    name: '',
    site_id: '',
    site_name: '',
    road_name: '',
    road_id: '',
    type: '',
    type_value: '',
    polygons: '',
  });

  // Filtering values (for the 3 Select components)
  const [filterValues, setFilterValues] = useState({
    site_id: '',
    road_id: '',
    type: '',
  });

  // --- Initial/Cleanup/Ref Effects ---
  useEffect(() => {
    if (searchRef.current) {
      searchRef.current.focus();
    }
  }, []);

  useEffect(() => {
    hasChangesUpdateRef.current = hasChangesUpdate;
    hasChangesCreateRef.current = hasChangesCreate;
    isNewClickedRef.current = isNewClicked;
    selectedEventRef.current = selectedEvent;
  }, [hasChangesUpdate, hasChangesCreate, isNewClicked, selectedEvent]);

  // --- Helpers ---
  const handleOpenModal = () => {
    if (formValues.type && (isNewClicked || isRowSelected)) {
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const detailEvent = useCallback((data: DetectionDetailData | undefined): EventFormValues => {
    return {
      detection_id: data?.detection_id.toString() || '',
      name: data?.name || '',
      site_id: data?.site_id.toString() || '',
      site_name: data?.site_name || '',
      road_id: data?.road_id.toString() || '',
      road_name: data?.road_name || '',
      type: data?.type || '',
      type_value: data?.type_value || '',
      polygons: data?.polygons || '',
    };
  }, []);

  const emptyDetail = () => {
    setFormValues({
      detection_id: '',
      name: '',
      site_id: '',
      site_name: '',
      road_name: '',
      road_id: '',
      type: '',
      type_value: '',
      polygons: '',
    });
  };

  const disableAllButtons = useCallback(() => {
    setButtonState({
      confirm: true,
      cancel: true,
      delete: true,
      create: true,
    });
  }, []);

  const enableInitialButtons = () => {
    disableAllButtons();
    setIsNewClicked(false);
    setButtonState((prevState) => ({
      ...prevState,
      create: false,
    }));
  };

  const enableUPDATEButtons = useCallback(() => {
    disableAllButtons();
    setButtonState((prevState) => ({
      ...prevState,
      cancel: false,
      delete: false,
      create: false,
    }));
  }, [disableAllButtons]);

  const enableRegisterButtons = () => {
    disableAllButtons();
    setButtonState((prevState) => ({
      ...prevState,
      cancel: false,
    }));
  };

  const reloadCallback = () => {
    enableInitialButtons();
    tbRef.current?.deselectRow();
    emptyDetail();
    setDisabledForm(true);
    setHasChangesUpdate(false);
    setIsNewClicked(false);
    setHasChangesCreate(false);
    setIsEnableModal(false);
    setIsRowSelected(false);
    setSelectedEvent({ Detection_ID: null });
  };

  // --- Hooks & Data Fetching ---
  const {
    detectionListData,
    detailDetectionData,
    createDetection,
    deleteDetection,
    updateDetection,
    siteRoad,
  } = useEventManagement({
    detectionId: selectedEvent?.Detection_ID || undefined,
    queryParams: queryParams,
    siteRoadParams: siteRoadParams,
    onDeleteSuccess: reloadCallback,
    onUpdateSuccess: () => {
      enableUPDATEButtons();
      setIsNewClicked(false);
      setHasChangesUpdate(false);
    },
    onCreateSuccess: (responseData) => {
      reloadCallback();
      const newID = responseData?.detection_id?.toString() ?? '';
      setNewId(newID);
    },
  });

  const pageData = detectionListData?.data;
  const data = pageData?.rows ?? [];
  const dataEvent = detailDetectionData?.data;

  // Columns built in-component so the "No" formatter can use the server total /
  // page / size (Tabulator's own pagination is disabled for remote paging).
  // "No" preserves the existing reverse numbering (newest row shows the highest).
  const scenarioEventColumns: ColumnDefinition<ScenarioEvent>[] = useMemo(
    () => [
      {
        title: 'No',
        formatter: (cell: CellComponent<ScenarioEvent>) => {
          const total = pageData?.total ?? 0;
          const globalIndex = (table.page - 1) * table.size + cell.getRow().getPosition(true);
          return total - globalIndex + 1;
        },
        width: 60,
        headerSort: false,
        ...commonColumnSettings,
      },
      {
        title: '이벤트 ID',
        field: 'detection_id',
        widthGrow: 1,
        headerSort: true,
        ...commonColumnSettings,
      },
      {
        title: '사이트',
        field: 'site_name',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnSettings,
      },
      {
        title: '접근로',
        field: 'road_name',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnSettings,
      },
      {
        title: '이벤트명',
        field: 'name',
        widthGrow: 1.5,
        headerSort: false,
        ...commonColumnSettings,
      },
      {
        title: '이벤트 유형',
        field: 'type_value',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnSettings,
      },
      {
        title: '업데이트 일시',
        field: 'updated_time',
        widthGrow: 1,
        headerSort: true,
        ...commonColumnSettings,
      },
    ],
    [pageData?.total, table.page, table.size]
  );

  const { commonListData } = useCommonCodes({ optionParams });
  const dataTypes = commonListData?.SCNR_TP;

  // const { siteRoad } = useSiteRoad({ siteRoadParams });
  const dataSitesRoads = siteRoad?.data;

  const { unmappedSiteRoad } = useUnmappedSiteRoad({ resource });
  const dataSitesRoadsEvents = unmappedSiteRoad?.data;

  useEffect(() => {
    if (dataEvent) {
      const data = detailEvent(dataEvent);
      setFormValues(data);
      setIsEnableModal(!!data.type);
      enableUPDATEButtons();
    }
  }, [dataEvent, detailEvent, enableUPDATEButtons]);

  // --- Filtering Logic for Selects ---
  const optionSite =
    dataSitesRoads?.sites?.map((site) => ({
      value: site.site_id.toString(),
      label: String(site.name),
    })) || [];

  const optionsRoad = filterValues.site_id
    ? dataSitesRoads?.sites
        ?.find((site) => site.site_id === Number(filterValues.site_id))
        ?.roads.map((road) => ({
          value: road.road_id.toString(),
          label: road.name,
        })) || []
    : [];

  const optionsEventType =
    dataTypes?.code?.map((datatype: string | null, index: number) => ({
      value: datatype ?? '',
      label: `${dataTypes.name[index] ?? ''}`,
    })) || [];

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;

    setFilterValues((prev) => {
      const updated = { ...prev, [name]: value };

      if (name === 'site_id') {
        updated.road_id = '';
        updated.type = '';
      }

      if (name === 'road_id') {
        updated.type = '';
      }

      return updated;
    });
  };

  // --- Search & Reset Handlers for Filtering V2 ---
  const handleSearch = useCallback(
    (searchInput: string | null = null) => {
      const { site_id, road_id, type } = filterValues;
      table.setFilters({
        input: searchInput || undefined,
        site_id: site_id || undefined,
        road_id: road_id || undefined,
        detection_type: type || undefined,
      });
    },
    [filterValues, table]
  );

  const handleReset = () => {
    setFilterValues({
      site_id: '',
      road_id: '',
      type: '',
    });
    table.reset();
  };

  // --- Detail Form Logic ---
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    let newValues = { ...formValues, [name]: value };
    let shouldEnableModal = isEnabledModal;

    if (name === 'site_id') {
      const selectedSite = dataSitesRoadsEvents?.sites?.find(
        (site) => site.site_id === Number(value)
      );

      newValues = {
        ...newValues,
        site_id: selectedSite?.site_id.toString() || '',
        site_name: selectedSite?.name || '',
        road_id: '',
        road_name: '',
        type: '',
        type_value: '',
        polygons: '',
      };
      shouldEnableModal = false;
    } else if (name === 'road_id') {
      const selectedSite = dataSitesRoadsEvents?.sites?.find(
        (site) => site.site_id === Number(newValues.site_id)
      );
      const selectedRoad = selectedSite?.roads?.find((road) => road.road_id === Number(value));

      newValues = {
        ...newValues,
        road_id: selectedRoad?.road_id.toString() || '',
        road_name: selectedRoad?.name || '',
        type: '',
        type_value: '',
        polygons: '',
      };
      shouldEnableModal = false;
    } else if (name === 'type') {
      const selectedSite = dataSitesRoadsEvents?.sites?.find(
        (site) => site.site_id === Number(newValues.site_id)
      );
      const selectedRoad = selectedSite?.roads?.find(
        (road) => road.road_id === Number(newValues.road_id)
      );
      const selectedType = (
        selectedRoad as typeof selectedRoad & {
          detection_types?: { type: string; type_value: string }[];
        }
      )?.detection_types?.find((dt) => dt.type === value);

      if (selectedType) {
        newValues.type = selectedType.type;
        newValues.type_value = selectedType.type_value;
        shouldEnableModal = true;
      } else {
        newValues.type = '';
        newValues.type_value = '';
        shouldEnableModal = false;
      }
    }

    setFormValues(newValues);
    setIsEnableModal(shouldEnableModal);

    if (isNewClickedRef.current) {
      setHasChangesCreate(true);
    } else if (selectedEventRef.current?.Detection_ID) {
      setHasChangesUpdate(true);
    }
  };

  // --- Tabulator & Row Selection ---
  const languageTabulator = () => {
    const datalanguage = {
      pagination: {
        first: t('cmn > first page'),
        first_title: t('cmn > first page'),
        last: t('cmn > last page'),
        last_title: t('cmn > last page'),
        prev: t('cmn > page before'),
        prev_title: t('cmn > page before'),
        next: t('cmn > next page'),
        next_title: t('cmn > next page'),
      },
    };
    return datalanguage;
  };

  // Button State (kept from original)
  const [buttonState, setButtonState] = useState({
    confirm: true,
    cancel: true,
    delete: true,
    create: false,
  });

  // Row Selected Function (kept from original)
  const handleRowSelected = useCallback(
    (row: RowComponent) => {
      const confirmAndSelect = (rowData: ScenarioEvent & Record<string, string | number>) => {
        tbRef.current?.deselectRow();
        row.select();
        row.getElement().classList.add('custom-hover');

        setSelectedEvent({
          Detection_ID: String(rowData.detection_id),
        });

        setFormValues((prevValues) => ({
          ...prevValues,
          name: String(rowData.name || ''),
          detection_id: String(rowData.detection_id || ''),
          type_value: String(rowData.type_value || ''),
          site_id: String(rowData.site_id || ''),
          site_name: String(rowData.site_name || ''),
          road_id: String(rowData.road_id || ''),
          road_name: String(rowData.road_name || ''),
          type: String(rowData.type || ''),
          polygons: String(rowData.polygons || ''),
        }));

        setDisabledForm(false);
        setHasChangesUpdate(false);
        setHasChangesCreate(false);
        setIsNewClicked(false);
        setIsRowSelected(true);
        setIsEnableModal(!!rowData.type);
        enableUPDATEButtons();
      };

      if (hasChangesCreateRef.current || hasChangesUpdateRef.current) {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        const message = new NoticeMessage(t('msg > flush confirm'), {
          mode: 'confirm',
        });
        message
          .confirmClicked()
          .then(() => {
            confirmAndSelect(row.getData());
          })
          .catch(() => {
            row.deselect();

            if (isNewClickedRef.current) {
              const prevId = selectedEventRef.current?.Detection_ID;
              if (prevId) {
                const prevRow = tbRef.current?.getRow(prevId);
                prevRow?.select();
                prevRow?.getElement().classList.add('custom-hover');
              }
            }
          });
      } else {
        confirmAndSelect(row.getData());
      }

      requestAnimationFrame(() => {
        if (nameRef.current) {
          nameRef.current.focus();
        }
      });
    },
    [t, enableUPDATEButtons]
  );

  // --- Button Handlers ---
  const handleNewButtonClick = () => {
    const action = () => {
      tbRef.current?.deselectRow();
      emptyDetail();
      enableRegisterButtons();
      setSelectedEvent({ Detection_ID: null });
      setDisabledForm(false);
      setHasChangesUpdate(false);
      setHasChangesCreate(false);
      setIsRowSelected(true);
      setIsNewClicked(true);
      setIsEnableModal(false);
    };

    if (hasChangesCreate || hasChangesUpdate) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), {
        mode: 'confirm',
      });
      message.confirmClicked().then(action);
    } else {
      action();
    }

    requestAnimationFrame(() => {
      if (nameRef.current) {
        nameRef.current.focus();
      }
    });
  };

  const handleCancelButtonClick = () => {
    const action = () => {
      tbRef.current?.deselectRow();
      emptyDetail();
      setIsNewClicked(false);
      setDisabledForm(true);
      enableInitialButtons();
      setHasChangesUpdate(false);
      setHasChangesCreate(false);
      setIsRowSelected(false);
      setIsEnableModal(false);
    };

    if (hasChangesUpdate || hasChangesCreate) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), {
        mode: 'confirm',
      });
      message.confirmClicked().then(action);
    } else {
      action();
    }
  };

  const handleRegistButtonClick = () => {
    const { name, site_id, road_id, type, polygons } = formValues;

    if (!name || !site_id || !road_id || !type || !polygons) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage('필수 값을 모두 입력해주세요.');
      return;
    }

    const newDetection: CreateDetectionRequest = {
      name,
      site_id: Number(site_id),
      road_id: Number(road_id),
      type,
      polygons: polygons,
    };

    createDetection(newDetection);
  };

  const handleConfirmButtonClick = () => {
    const { detection_id, name, site_id, road_id, type, polygons } = formValues;

    if (!detection_id || !name || !site_id || !road_id || !type) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage('필수 값을 모두 입력해주세요.');
      return;
    }

    const updatedDetection: UpdateDetectionRequest = {
      name,
      site_id: Number(site_id),
      road_id: Number(road_id),
      type,
      polygons: polygons || '',
    };

    updateDetection(updatedDetection);
  };

  const handleDeleteButtonClick = () => {
    const idToDelete = selectedEvent?.Detection_ID;
    if (!idToDelete) return;

    if (dataEvent?.mapped_scenario_name) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(
        '해당 이벤트가 이미 시나리오에 매핑되어 사용되고 있습니다. 먼저 매핑을 해제해주세요'
      );
      return;
    }

    // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
    const message = new NoticeMessage(t('msg > delete confirm'), {
      mode: 'confirm',
    });
    message.confirmClicked().then(() => {
      deleteDetection(idToDelete);
    });
  };

  const handleSavePolygons = (polygonString: string) => {
    setFormValues((prev) => ({
      ...prev,
      polygons: polygonString,
    }));

    if (isNewClickedRef.current) {
      setHasChangesCreate(true);
    } else {
      setHasChangesUpdate(true);
    }
  };

  // Log Data
  const logData = dataEvent
    ? [
        { label: t('cmn > registered by'), value: dataEvent.registered_by },
        { label: t('cmn > registered time'), value: dataEvent.registered_time },
        { label: t('cmn > updated by'), value: dataEvent.updated_by },
        { label: t('cmn > updated time'), value: dataEvent.updated_time },
      ]
    : [];

  // Tabulator Options (moved to keep functions together)
  const optionsTabulator = {
    // Server-side pagination: Tabulator's own pager is off; <TablePagination> drives it.
    pagination: false,
    rowHeight: 41,
    selectableRows: false,
    movableRows: false,
    index: 'detection_id',
    resizableRows: false,
    locale: 'ko',
    langs: {
      ko: languageTabulator(),
    },
    selectableRowsCheck: (row: RowComponent) => {
      return !row.getElement().classList.contains('tabulator-selected');
    },
    placeholder: "<div style='text-align:center;'>No Data</div>",
  };

  return (
    <>
      <section className="wrap">
        <div className="header-title">
          <h3>시스템 관리</h3>
          <h3>&gt;</h3>
          <h3>이벤트 관리</h3>
        </div>

        <ContainerCard>
          <Filtering
            searchRef={searchRef}
            placeholder="이벤트명"
            disableFiltering={true}
            showLabel={false}
            disableResetButton={false}
            onReset={handleReset}
            onSearch={(searchInput) => handleSearch(searchInput)}
            customWidthSelect="flex flex-1 basis-[700px]"
            customWidthInput="flex flex-1"
          >
            <div className="flex flex-row gap-[15px] w-full">
              <div className="flex w-full gap-[15px]">
                <span className="title3bold w-[97px] text-au-neutral-4 py-[9px] flex-grow whitespace-nowrap">
                  사이트
                </span>
                <Select
                  name="site_id"
                  value={filterValues?.site_id || ''}
                  onChange={handleFilterChange}
                  options={[{ value: '', label: '전체' }, ...optionSite]}
                  customWidthSelect="w-full"
                  className="cursor-pointer"
                />
              </div>
              <div className="flex w-full gap-[15px]">
                <span className="title3bold w-[97px] text-au-neutral-4 py-[9px] flex-grow whitespace-nowrap">
                  접근로
                </span>
                <Select
                  className="cursor-pointer"
                  name="road_id"
                  customWidthSelect="w-full"
                  value={filterValues?.road_id || ''}
                  onChange={handleFilterChange}
                  disabled={!filterValues.site_id}
                  options={[{ value: '', label: '전체' }, ...optionsRoad]}
                />
              </div>
              <div className="flex w-full gap-[15px]">
                <span className="title3bold w-[97px] text-au-neutral-4 py-[9px] flex-grow whitespace-nowrap">
                  이벤트 유형
                </span>
                <Select
                  className="cursor-pointer"
                  name="type"
                  customWidthSelect="w-full"
                  value={filterValues?.type || ''}
                  options={[{ value: '', label: '전체' }, ...optionsEventType]}
                  onChange={handleFilterChange}
                />
              </div>
            </div>
          </Filtering>
        </ContainerCard>

        <ContainerCard>
          <div className="table-container relative">
            <ReactTabulator
              id="scenario-event-table"
              data={data}
              columns={scenarioEventColumns}
              layout={'fitColumns'}
              className="tabulator-custom w-full"
              options={optionsTabulator}
              onRef={(r: { current: Tabulator | null }) => {
                tbRef.current = r.current;
              }}
              events={{
                rowClick: (_e: UIEvent, row: RowComponent) => handleRowSelected(row),
                // Header click → delegate sorting to the server (Tabulator also re-sorts
                // the current page locally by the same key, so it stays consistent).
                // Guard against programmatic re-sorts that fire on every data load.
                dataSorted: remoteSortHandler(table, DETECTION_SORT_FIELD_MAP),
                tableBuilt: () => {
                  const rowToSelectId = newId || selectedEvent?.Detection_ID;

                  if (rowToSelectId) {
                    const row = tbRef.current?.getRow(rowToSelectId);
                    if (row) {
                      tbRef.current?.scrollToRow(row, 'bottom', true);
                      row.select();
                      row.getElement().classList.add('custom-hover');

                      if (newId) {
                        handleRowSelected(row);
                        setNewId('');
                      }
                    }
                  }
                },
              }}
            />

            {/* Server-side Pagination Controls - Tabulator style */}
            <div className="tabulator-footer">
              <div className="tabulator-paginator">
                <button
                  type="button"
                  className="tabulator-page"
                  onClick={() => table.setPage(1)}
                  disabled={table.page === 1}
                >
                  {t('cmn > first page')}
                </button>
                <button
                  type="button"
                  className="tabulator-page"
                  onClick={() => table.setPage(table.page - 1)}
                  disabled={table.page === 1}
                >
                  {t('cmn > page before')}
                </button>
                <span className="tabulator-pages">
                  {Array.from({ length: Math.min(5, pageData?.total_pages ?? 0) }, (_, i) => {
                    const totalPages = pageData?.total_pages ?? 0;
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (table.page <= 3) {
                      pageNum = i + 1;
                    } else if (table.page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = table.page - 2 + i;
                    }
                    return (
                      <button
                        type="button"
                        key={pageNum}
                        className={`tabulator-page ${table.page === pageNum ? 'active' : ''}`}
                        onClick={() => table.setPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </span>
                <button
                  type="button"
                  className="tabulator-page"
                  onClick={() => table.setPage(table.page + 1)}
                  disabled={table.page === (pageData?.total_pages ?? 0)}
                >
                  {t('cmn > next page')}
                </button>
                <button
                  type="button"
                  className="tabulator-page"
                  onClick={() => table.setPage(pageData?.total_pages ?? 1)}
                  disabled={table.page === (pageData?.total_pages ?? 0)}
                >
                  {t('cmn > last page')}
                </button>
              </div>
              <div style={{ padding: '0 20px 0 0', textAlign: 'right' }}>
                총 {pageData?.total ?? 0} 건
              </div>
            </div>
          </div>
        </ContainerCard>

        <ContainerCard className="flex flex-col">
          <div className="box-scenario-event flex flex-col gap-3">
            <div className="grid grid-cols-3 gap-[50px]">
              <DetailForm
                className="items-center!"
                label="이벤트 ID"
                value={formValues.detection_id || ''}
                name="detection_id"
                disabled={disabledId}
                onChange={handleInputChange}
              />
              <DetailForm
                className="items-center!"
                label="이벤트명"
                required={true}
                value={formValues.name}
                disabled={disabledForm}
                name="name"
                onChange={handleInputChange}
                formRef={nameRef}
              />
              <DetailForm
                inputType={'select'}
                className="items-center!"
                label="사이트"
                required={true}
                value={formValues.site_id}
                name="site_id"
                disabled={disabledForm}
                onChange={handleInputChange}
                optionSelect={[
                  {
                    value: formValues.site_id || '',
                    label: formValues.site_name || '',
                  },
                  ...(
                    dataSitesRoadsEvents?.sites?.map((site) => ({
                      value: site.site_id.toString(),
                      label: `${site.name}`,
                    })) || []
                  ).filter((opt) => opt.value !== formValues.site_id),
                ]}
              />
            </div>
            <div className="grid grid-cols-3 gap-[50px]">
              <DetailForm
                inputType="select"
                className="items-center!"
                label="접근로"
                name="road_id"
                required={true}
                value={formValues.road_id}
                disabled={disabledForm || !formValues.site_id}
                onChange={handleInputChange}
                optionSelect={[
                  {
                    value: formValues.road_id || '',
                    label: formValues.road_name || '',
                  },
                  ...(
                    dataSitesRoadsEvents?.sites
                      ?.find((site) => site.site_id === Number(formValues.site_id))
                      ?.roads?.map((road) => ({
                        value: road.road_id.toString(),
                        label: `${road.name}`,
                      })) || []
                  ).filter((opt) => opt.value !== formValues.road_id),
                ]}
              />
              <DetailForm
                inputType="select"
                className="items-center"
                label="유형"
                name="type"
                value={formValues.type}
                required={true}
                disabled={disabledForm || !formValues.road_id}
                onChange={handleInputChange}
                optionSelect={[
                  {
                    value: formValues.type || '',
                    label: formValues.type_value || '',
                  },
                  ...(
                    (
                      dataSitesRoadsEvents?.sites
                        ?.find((site) => site.site_id === Number(formValues.site_id))
                        ?.roads?.find((road) => road.road_id === Number(formValues.road_id)) as
                        | {
                            detection_types?: {
                              type: string;
                              type_value: string;
                            }[];
                          }
                        | undefined
                    )?.detection_types?.map((detection_type) => ({
                      value: detection_type.type,
                      label: `${detection_type.type_value}`,
                    })) || []
                  ).filter((opt) => opt.value !== formValues.type),
                ]}
              />
              <DetailForm
                className="items-center!"
                label="검지 영역 설정"
                required={true}
                showInput={false}
                disabled={!isEnabledModal || disabledForm}
              >
                <button
                  type="button"
                  onClick={handleOpenModal}
                  disabled={!isEnabledModal || disabledForm}
                  className={`body1bold px-[20px] py-[3px] text-[#FEFEFE] ${
                    !isEnabledModal || disabledForm
                      ? 'bg-[#8F9193] cursor-not-allowed opacity-50'
                      : 'bg-[#3D6B85] hover:opacity-80 cursor-pointer'
                  }`}
                >
                  Open Modal
                </button>
              </DetailForm>
            </div>

            <hr className="border-t border-gray-300" />

            <div className="flex items-center justify-between gap-4 w-full">
              <div className="flex-1">{disabledForm ? null : <LogList logs={logData} />}</div>

              <div className="flex-none">
                <ButtonGroup
                  onClickDelete={handleDeleteButtonClick}
                  onClickNew={handleNewButtonClick}
                  onClickCancel={handleCancelButtonClick}
                  onClickRegist={handleRegistButtonClick}
                  onClickConfirm={handleConfirmButtonClick}
                  isNewClicked={isNewClicked}
                  cancelButtonState={buttonState.cancel}
                  confirmButtonState={hasChangesUpdate ? false : buttonState.confirm}
                  deleteButtonState={buttonState.delete}
                  newButtonState={hasChangesCreate ? false : buttonState.create}
                />
              </div>
            </div>
          </div>
        </ContainerCard>
      </section>

      <ModalDetectionArea
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        formData={{
          ...formValues,
          site_id: Number(formValues.site_id),
          road_id: Number(formValues.road_id),
          polygons: formValues.polygons,
        }}
        onSavePolygons={handleSavePolygons}
      />
    </>
  );
};

export default EventManagementPage;
